import {defineStore} from "pinia";
import {computed, ref} from "vue";
import {decryptData, encryptData} from "@/utilities/crypto";
import {EXPIRY_TOKEN_VARIABLE, TOKEN_VARIABLE, USER_INFO_VARIABLE} from "@/config/constants";
import {instance} from "@/config/request.config";
import Swal from "sweetalert2";
import payloadBuilder from "@/utilities/payload-builder";
import {useGlobalStore} from "@/store/useGlobalStore";

export const useAuthStore = defineStore("authStore", () => {
    const token = ref(decryptData(localStorage.getItem(TOKEN_VARIABLE)))
    const expiresAt = ref(decryptData(localStorage.getItem(EXPIRY_TOKEN_VARIABLE)))
    const userInfo = ref(decryptData(localStorage.getItem(USER_INFO_VARIABLE)))
    const globalStore = useGlobalStore()
    const isPermissionLoaded = ref(false)

    const getUserToken = computed(() => token.value)
    const getTokenExpiresAt = computed(() => expiresAt.value)
    const getUserLogInfo = computed(() => userInfo.value)

    const getUserInfo = computed(() => {
        let details = decryptData(localStorage.getItem(USER_INFO_VARIABLE))
        return JSON.parse(details)
    })

    function setLocalStorage(tokenVal, expiry, user_info) {
        token.value = tokenVal
        expiresAt.value = expiry
        userInfo.value = user_info
        if (tokenVal) {
            localStorage.setItem(TOKEN_VARIABLE, encryptData(tokenVal))
            localStorage.setItem(EXPIRY_TOKEN_VARIABLE, encryptData(expiry))
        } else {
            localStorage.removeItem(TOKEN_VARIABLE)
            localStorage.removeItem(EXPIRY_TOKEN_VARIABLE)
        }

        if (user_info) {
            localStorage.setItem(USER_INFO_VARIABLE, encryptData(JSON.stringify(user_info)))
        } else {
            localStorage.removeItem(USER_INFO_VARIABLE)
        }
    }

    function clearAuth() {
        token.value = null
        expiresAt.value = null
        userInfo.value = null
        localStorage.clear()
    }

    const isLoggedIn = computed(() => {
        return !!token.value && !!expiresAt.value && new Date(expiresAt.value) > new Date()
    })

    async function login(credentials) {
        try {
            const response = await instance.post("auth", credentials);
            if (response) {
                if(response.data.statusCode) {
                    return {
                        'is_error': true,
                        'error_message': response.data.response,
                        'data': response.data
                    }
                }
                const { token, expiresAt, loggedUser } = response.data.data

                setLocalStorage(token, expiresAt, loggedUser)
                return {
                    'is_error': false,
                    'error_message': '',
                    'data': response.data
                }
            }
        }
        catch(error) {
            clearAuth()
            return {
                'is_error': true,
                'error_message': error.response.data.data.response,
                'data': error.response.data
            }
        }
    }

    async function logout(condition) {
        try {
            // TODO: request footprint for logout
        } catch (error) {
            console.error('Logout failed!', error)
        } finally {
            clearAuth()
            console.log('Successfully logged out!')
        }
    }

    const getCurrentUser = async() => {
        let data = {}
        try {
            let details = decryptData(localStorage.getItem(USER_INFO_VARIABLE))
            if(details) details = JSON.parse(details)

            const username = details?.user_name ?? ''

            const response = await payloadBuilder('user', 'get')
                .addPayload('conditions', {code: username}).execute()
            if(response) {
                data = response.data.data ?? {}
                updateLoggedUser(data)
            }
        } catch (e) {
            console.error(e)
        }

        return data
    }

    const updateLoggedUser = (data) => {
        let details = decryptData(localStorage.getItem(USER_INFO_VARIABLE))
        if(details) details = JSON.parse(details)

        if(data) {
            for(const key of Object.keys(data)) {
                if(!details.hasOwnProperty(key))  {
                    details[key] = ''
                }
                details[key] = data[key] ?? details[key]
            }

            localStorage.setItem(USER_INFO_VARIABLE, encryptData(JSON.stringify(details)))
            localStorage.setItem('branches', encryptData(JSON.stringify(details?.branch ?? [])))
            globalStore.assigned_branch = details?.branch ?? []

            updateLoggedUserDefaultBranch()
        }
    }

    const updateLoggedUserDefaultBranch = () => {
        if(globalStore.assigned_branch.length === 1) globalStore.default_branch = globalStore.assigned_branch[0] ?? ''
    }

    async function getPermission(type) {
        try {
            isPermissionLoaded.value = false
            let details = decryptData(localStorage.getItem(USER_INFO_VARIABLE))
            if(details) details = JSON.parse(details)

            const user = details?.user_name ?? ''
            const conditions = { params: { code: user, type: 'current_user' } }
            const response = await payloadBuilder('permission', 'get')
                .addPayload('conditions', conditions).execute()
            Swal.close()
            if(response) {
                if(response.data.statusCode) {
                    return await alert.toastNotification('Failed to get permission. ' + response.data.data.error ?? '' + ' Please try again.', 'Failed to get permission', 'error')
                }

                const perm_list = response.data.data.list ?? {}
                const sys_perm = response.data.data.access ?? {}

                localStorage.setItem('permission_list', encryptData(JSON.stringify(perm_list)))
                localStorage.setItem('permission_access', encryptData(JSON.stringify(sys_perm)))
            }
        } catch (error) {
            console.log(error)
            Swal.close()
            return await alert.toastNotification('Failed to get permission. ' + error ?? '' + ' Please get again.', 'Failed to get permission', 'error')
        } finally {
            isPermissionLoaded.value = true
        }
    }


    function getAccess(type = 'module', module = null, func = null, sys = 'acctg') {
        let permission = localStorage.getItem('permission_access')
        if(permission) {
            permission = JSON.parse(decryptData(permission))
            const sys_permission = permission[sys] ?? {}

            if(sys_permission) {
                if(type === 'module') return sys_permission?.main_modules?.includes(module)
                else if(type === 'fn') return sys_permission?.functions?.[module]?.access?.includes(func)
            }

            return false
        }
        return false
    }

    return {
        clearAuth,
        login,
        logout,
        isLoggedIn,
        getUserLogInfo,
        getUserToken,
        getTokenExpiresAt,
        getUserInfo,
        getPermission,
        getCurrentUser,
        isPermissionLoaded,
        getAccess
    }
})